import { clearApiCache, getCachedData, setCachedData } from "../services/cache";
import { decodeTokenSafely } from "../core/jwt-decode";
import {
  buildAuthHeaders,
  resolveRequestAuth,
  sandboxBodyIdentity,
  sandboxQueryIdentity,
} from "./sandbox";
import { toLanguageCode } from "./language";
import { toSubmitPurposes } from "./submit-payload";
import { parseApiError } from "../shared/api-errors";
import { withIdempotencyKey } from "../shared/idempotency";
import type {
  ConsentContent,
  ConsentEventPayload,
  FetchConsentContentParams,
  FetchConsentContentPublicParams,
  FetchTTSAudioUrlsParams,
  InitiateGuardianVerificationParams,
  InitiateGuardianVerificationResponse,
  SubmitConsentEventParams,
  SubmitConsentEventPublicParams,
  TTSAudioUrlsResponse,
  VerifyGuardianStatusParams,
  VerifyGuardianStatusResponse,
} from "./types";
import { DEFAULT_CONSENT_API_BASE_URL } from "../constants";

const BASE_URL = DEFAULT_CONSENT_API_BASE_URL;

// ─── Modal: fetch notice (authenticated, JWT-decoded) ───

export const fetchConsentContent = async ({
  noticeId,
  accessToken,
  baseUrl,
  language = "en",
  specific_uuid,
  validate_against = "all",
  include_fully_consented_data = false,
  signal,
  ...sandboxParams
}: FetchConsentContentParams & { signal?: AbortSignal }): Promise<ConsentContent> => {
  if (!noticeId || !validate_against) {
    throw new Error("noticeId is required");
  }
  if (!accessToken && !sandboxParams.token) {
    throw new Error("accessToken or token is required");
  }

  const { organisationUuid: ORG, workspaceUuid: WS, sandbox } = resolveRequestAuth(
    accessToken,
    sandboxParams
  );

  const apiBaseUrl = baseUrl || BASE_URL;

  // Include org + workspace in the sandbox cache identity so two sessions
  // sharing an identity but scoped to different orgs never collide.
  const cacheIdentity = sandbox
    ? `sandbox:${sandbox.organisationUuid}:${sandbox.workspaceUuid}:${sandbox.ucic || sandbox.email || sandbox.mobile}`
    : accessToken;
  const cacheKey = `${cacheIdentity}-${noticeId}-${validate_against}-${language}-${specific_uuid || ""}-${include_fully_consented_data}`;

  if (validate_against === "all" && !specific_uuid) {
    const cached = getCachedData(cacheKey);
    if (cached) return cached;
  }

  const url = new URL(`${apiBaseUrl}/public/organisations/${ORG}/workspaces/${WS}/notices/${noticeId}`);
  if (specific_uuid) url.searchParams.append("specific_uuid", specific_uuid);
  url.searchParams.append("validate_against", validate_against);
  if (include_fully_consented_data) url.searchParams.append("include_fully_consented_data", "true");
  // Sandbox mode: the acting identity travels as query params
  // (primary_email / primary_mobile); the credential is the X-Consent-Token header.
  if (sandbox) {
    for (const [key, value] of Object.entries(sandboxQueryIdentity(sandbox))) {
      if (value) url.searchParams.append(key, value);
    }
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      ...buildAuthHeaders(accessToken, sandbox),
      "Accept-Language": language,
    },
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, "Could not load the consent notice. Please try again.");
  }

  const data: ConsentContent = await response.json();

  if (validate_against === "all" && !specific_uuid) {
    setCachedData(cacheKey, data);
  }

  return data;
};

// ─── Inline: fetch notice (public, no auth) ───

export const fetchConsentContentPublic = async ({
  org_uuid,
  workspace_uuid,
  notice_uuid,
  accessToken,
  baseUrl,
  language = "en",
  specific_uuid,
}: FetchConsentContentPublicParams): Promise<ConsentContent> => {
  const apiBaseUrl = baseUrl || BASE_URL;
  const noticesUrl = `${apiBaseUrl}/public/organisations/${org_uuid}/workspaces/${workspace_uuid}/notices`;

  const url = new URL(
    accessToken ? `${noticesUrl}/${notice_uuid}` : `${noticesUrl}/get-notice/${notice_uuid}`
  );
  if (specific_uuid) url.searchParams.append("specific_uuid", specific_uuid);

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  if (!response.ok) {
    throw await parseApiError(response, "Could not load the consent notice. Please try again.");
  }

  return await response.json();
};

// ─── Modal: submit consent (JWT-decoded) ───

export const submitConsentEvent = async ({
  accessToken,
  baseUrl,
  ledgerBaseUrl,
  noticeUuid,
  purposes,
  declined,
  meta_data,
  guardianVerificationReference,
  selfDeclaredAdult,
  language,
  signal,
  ...sandboxParams
}: SubmitConsentEventParams & { signal?: AbortSignal }): Promise<void> => {
  if (!noticeUuid || !Array.isArray(purposes)) {
    throw new Error("noticeUuid and purposes array are required");
  }
  if (!accessToken && !sandboxParams.token) {
    throw new Error("accessToken or token is required");
  }

  const { organisationUuid: ORG, workspaceUuid: WS, sandbox } = resolveRequestAuth(
    accessToken,
    sandboxParams
  );

  // Route the write to the Go ledger when configured; fall back to the consent
  // server so existing integrations that only pass baseUrl keep working.
  const apiBaseUrl = ledgerBaseUrl || baseUrl || BASE_URL;

  const payload: ConsentEventPayload = {
    notice_uuid: noticeUuid,
    purposes: toSubmitPurposes(purposes),
    select_all_mandatory: false,
    source: "WEB",
    declined: Boolean(declined),
    meta_data: meta_data ? { specific_uuid: meta_data.specific_uuid, minor_age: meta_data.minor_age } : undefined,
    guardian_verification_reference: guardianVerificationReference,
    self_declared_adult: selfDeclaredAdult,
    language: toLanguageCode(language),
  };

  // Sandbox mode: merge the acting test identity into the body (the server has
  // no subject header; it namespaces this identity `test::`).
  const requestBody = sandbox ? { ...payload, ...sandboxBodyIdentity(sandbox) } : payload;

  const url = `${apiBaseUrl}/public/organisations/${ORG}/workspaces/${WS}/submit-consent`;
  const serialisedBody = JSON.stringify(requestBody);

  const response = await withIdempotencyKey(url, serialisedBody, (idempotencyHeaders) =>
    fetch(url, {
      method: "POST",
      headers: {
        ...buildAuthHeaders(accessToken, sandbox),
        "Content-Type": "application/json",
        ...idempotencyHeaders,
      },
      body: serialisedBody,
      signal,
    })
  );

  if (!response.ok) {
    throw await parseApiError(response, "Could not submit your consent. Please try again.");
  }

  clearApiCache();
};

// ─── Inline: submit consent (direct org/workspace) ───

export const submitConsentEventPublic = async ({
  org_uuid,
  workspace_uuid,
  accessToken,
  baseUrl,
  ledgerBaseUrl,
  noticeUuid,
  purposes,
  declined,
  meta_data,
  language,
}: SubmitConsentEventPublicParams): Promise<void> => {
  // Route the write to the Go ledger when configured; fall back to the consent
  // server so existing integrations that only pass baseUrl keep working.
  const apiBaseUrl = ledgerBaseUrl || baseUrl || BASE_URL;

  const payload: ConsentEventPayload = {
    notice_uuid: noticeUuid,
    purposes: toSubmitPurposes(purposes),
    select_all_mandatory: false,
    source: "WEB",
    declined,
    meta_data,
    language: toLanguageCode(language),
  };

  const url = `${apiBaseUrl}/public/organisations/${org_uuid}/workspaces/${workspace_uuid}/submit-consent`;
  const serialisedBody = JSON.stringify(payload);

  const response = await withIdempotencyKey(url, serialisedBody, (idempotencyHeaders) =>
    fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        ...idempotencyHeaders,
      },
      body: serialisedBody,
    })
  );

  if (!response.ok) {
    throw await parseApiError(response, "Could not submit your consent. Please try again.");
  }
};

// ─── TTS Audio ───

export const fetchTTSAudioUrls = async ({
  accessToken,
  baseUrl,
  ledgerBaseUrl,
  endpointMode,
  noticeUuid,
  language,
  signal,
  ...sandboxParams
}: FetchTTSAudioUrlsParams & { signal?: AbortSignal }): Promise<TTSAudioUrlsResponse> => {
  if ((!accessToken && !sandboxParams.token) || !noticeUuid || !language) {
    throw new Error("accessToken (or token), noticeUuid, and language are required");
  }

  const { organisationUuid: ORG, workspaceUuid: WS, sandbox } = resolveRequestAuth(
    accessToken,
    sandboxParams
  );

  // Prefer the Go ledger for audio. Outside host mode a sandbox session stays
  // on the consent server, as in earlier releases; in host mode the ledger
  // serves sandbox audio too.
  const ledgerServesAudio = !sandbox || endpointMode === "host";
  const apiBaseUrl = (ledgerServesAudio ? ledgerBaseUrl : null) || baseUrl || BASE_URL;
  const url = new URL(
    `${apiBaseUrl}/public/organisations/${ORG}/workspaces/${WS}/notices/${noticeUuid}/audio/${language}`
  );
  // Sandbox mode: the acting identity travels as query params
  // (primary_email / primary_mobile); the credential is the X-Consent-Token header.
  if (sandbox) {
    for (const [key, value] of Object.entries(sandboxQueryIdentity(sandbox))) {
      if (value) url.searchParams.append(key, value);
    }
  }

  const response = await fetch(url.toString(), {
    method: "GET",
    headers: {
      ...buildAuthHeaders(accessToken, sandbox),
      Accept: "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw await parseApiError(response, "Could not load audio narration. Please try again.");
  }

  return await response.json();
};

// ─── Guardian Verification ───

export const initiateGuardianVerification = async ({
  accessToken,
  baseUrl,
  guardianName,
  guardianContact,
  guardianRelationship,
  frontendCallbackUrl,
  metadata,
  signal,
}: InitiateGuardianVerificationParams): Promise<InitiateGuardianVerificationResponse> => {
  // Guardian verification is JWT-only: sandbox sessions have no minor-notice /
  // guardian support (the server 403s them, and C1 blocks minor notices at
  // render), so no sandbox identity is wired through here.
  if (!accessToken) throw new Error("accessToken is required");
  if (!guardianName?.trim()) throw new Error("Guardian name is required");
  if (!guardianContact?.trim()) throw new Error("Guardian contact is required");
  if (!guardianRelationship?.trim()) throw new Error("Guardian relationship is required");

  const decodedToken = decodeTokenSafely(accessToken);
  if (!decodedToken) throw new Error("Invalid access token");
  const { organisation_uuid: ORG, workspace_uuid: WS } = decodedToken;
  if (!ORG || !WS) throw new Error("Invalid token: missing organization or workspace UUID");

  const apiBaseUrl = baseUrl || BASE_URL;

  const payload: Record<string, any> = {
    guardian_name: guardianName.trim(),
    guardian_contact: guardianContact.trim(),
    guardian_relationship: guardianRelationship.trim(),
  };
  if (frontendCallbackUrl) payload.frontend_callback_url = frontendCallbackUrl;
  if (metadata) payload.metadata = metadata;

  const response = await fetch(
    `${apiBaseUrl}/public/organisations/${ORG}/workspaces/${WS}/guardian/initiate-verification`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal,
    }
  );

  if (!response.ok) {
    throw await parseApiError(response, "Could not start guardian verification. Please try again.");
  }

  const responseData = await response.json();
  const detail = responseData.detail || responseData;

  if (detail.already_verified) {
    return {
      success: true,
      guardian_info_uuid: detail.guardian_info_uuid,
      already_verified: true,
      verification_reference: detail.verification_reference,
    };
  }

  if (!detail.session_token || !detail.digilocker_redirect_url) {
    throw new Error("Invalid response: missing session_token or digilocker_redirect_url");
  }

  return {
    success: detail.success ?? true,
    guardian_info_uuid: detail.guardian_info_uuid,
    already_verified: false,
    session_token: detail.session_token,
    digilocker_redirect_url: detail.digilocker_redirect_url,
    expires_at: detail.expires_at,
  };
};

export const verifyGuardianStatus = async ({
  accessToken,
  baseUrl,
  sessionToken,
  signal,
}: VerifyGuardianStatusParams): Promise<VerifyGuardianStatusResponse> => {
  // Guardian verification is JWT-only: sandbox sessions have no minor-notice /
  // guardian support (the server 403s them, and C1 blocks minor notices at
  // render), so no sandbox identity is wired through here.
  if (!accessToken || !sessionToken) {
    throw new Error("accessToken and sessionToken are required");
  }

  const decodedToken = decodeTokenSafely(accessToken);
  if (!decodedToken) throw new Error("Invalid access token");
  const { organisation_uuid: ORG, workspace_uuid: WS } = decodedToken;
  if (!ORG || !WS) throw new Error("Invalid token: missing organization or workspace UUID");

  const apiBaseUrl = baseUrl || BASE_URL;

  const response = await fetch(
    `${apiBaseUrl}/public/organisations/${ORG}/workspaces/${WS}/guardian/verify-status`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ session_token: sessionToken }),
      signal,
    }
  );

  if (!response.ok) {
    throw await parseApiError(response, "Could not check verification status. Please try again.");
  }

  const responseData = await response.json();
  const detail = responseData.detail || responseData;

  return {
    status: detail.status ?? "in_progress",
    guardian_info_uuid: detail.guardian_info_uuid ?? null,
    verification_reference: detail.verification_reference ?? null,
    guardian_details: detail.guardian_details ?? null,
    error: detail.error ?? null,
    error_code: detail.error_code ?? null,
    can_retry: detail.can_retry ?? true,
  };
};
